/**
 * Spec Phase 5 Slice 5D — ActionBar (Generate now live).
 *
 * Per Phase 5 brief §6:
 *   - Generate is wired live for the active section. Click triggers
 *     the store's generateSection action.
 *   - On click, button transitions to "Generating…" with a
 *     spinning icon. Disabled during the call.
 *   - For non-empty sections, the store returns the
 *     confirm_overwrite branch and the parent surfaces the
 *     OverwriteConfirmDialog.
 *   - For frozen sections, click is a no-op + toast.
 *   - For auto-generated sections, the button is disabled
 *     (auto sections render placeholder content; Phase 7 wires
 *     the live render).
 *   - "Steering ▾" expandable textarea below the action bar lets
 *     the drafter add guidance before clicking Generate. Collapses
 *     + clears on success.
 *
 * Expand / Tighten / Verify remain disabled — they wire in Phases
 * 6 and 8 respectively. Prev / Next stay live (added in Slice 4D).
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  ShieldCheck,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useSpecStore } from '@/store/specStore';
import OverwriteConfirmDialog from './OverwriteConfirmDialog';
import ConfirmDialog from './ConfirmDialog';

const btn =
  'inline-flex h-8 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 ' +
  'disabled:hover:bg-white transition-colors';

const primaryBtn =
  'inline-flex h-8 items-center justify-center gap-1 rounded-sm bg-zinc-900 ' +
  'px-3 text-xs font-medium text-white hover:bg-zinc-800 disabled:opacity-50 ' +
  'disabled:hover:bg-zinc-900 transition-colors';

export default function ActionBar(): JSX.Element {
  // PERF-F01 (Phase 26 3-E) — 21 individual ``useSpecStore``
  // subscriptions collapsed into a single ``useShallow``
  // selector. Each prior call was its own Zustand subscription;
  // every spec-state change fired 21 independent re-render
  // checks against ActionBar before React's bail-out kicked in.
  // ``useShallow`` runs ONE shallow-equal comparison against the
  // returned object and skips the re-render when no subscribed
  // key changed. Mirrors the pattern already used at
  // SpecPage.tsx:108.
  const {
    sections,
    activeSectionId,
    setActiveSection,
    generateStatus,
    activeParagraphId,
    paragraphMutationStatus,
    expandParagraph,
    tightenParagraph,
    pendingOverwrite,
    pendingRegenerateWithSteering,
    setPendingRegenerateWithSteering,
    generateSection,
    confirmGenerateOverwrite,
    cancelGenerateOverwrite,
    applyNlEdit,
    showToast,
    verifySection,
    sectionVerifyStatus,
    runExaminerReview,
    examinerStatus,
    resetSection,
    generateEverythingStatus,
  } = useSpecStore(useShallow((s) => ({
    sections: s.sections,
    activeSectionId: s.activeSectionId,
    setActiveSection: s.setActiveSection,
    generateStatus: s.generateStatus,
    activeParagraphId: s.activeParagraphId,
    paragraphMutationStatus: s.paragraphMutationStatus,
    expandParagraph: s.expandParagraph,
    tightenParagraph: s.tightenParagraph,
    pendingOverwrite: s.pendingOverwrite,
    pendingRegenerateWithSteering: s.pendingRegenerateWithSteering,
    setPendingRegenerateWithSteering: s.setPendingRegenerateWithSteering,
    generateSection: s.generateSection,
    confirmGenerateOverwrite: s.confirmGenerateOverwrite,
    cancelGenerateOverwrite: s.cancelGenerateOverwrite,
    applyNlEdit: s.applyNlEdit,
    showToast: s.showToast,
    verifySection: s.verifySection,
    sectionVerifyStatus: s.sectionVerifyStatus,
    runExaminerReview: s.runExaminerReview,
    examinerStatus: s.examinerStatus,
    resetSection: s.resetSection,
    generateEverythingStatus: s.generateEverythingStatus,
  })));
  const geRunning =
    generateEverythingStatus?.status === 'queued'
    || generateEverythingStatus?.status === 'running';

  const [steeringOpen, setSteeringOpen] = useState(false);
  const [steeringText, setSteeringText] = useState('');
  const [resetting, setResetting] = useState(false);
  // UX-F01 (Phase 27 3-F) — drives the styled ConfirmDialog that
  // replaces window.confirm() for Reset Section. Null when no
  // confirm is in flight; truthy while the dialog is open.
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  // Steering-vs-regenerate disambiguation. The drafter has often
  // typed steering text into the panel but then clicked the
  // top-bar "Regenerate" button — silently using or ignoring the
  // steering was the source of "I didn't expect this output"
  // confusion. When the top-bar Regenerate fires with non-empty
  // steering AND not from the auto-prefill banner CTA (which is
  // an explicit consent path), pause and let the drafter choose.
  const [steeringChoiceOpen, setSteeringChoiceOpen] = useState(false);
  // After a successful generate that USED steering, ask the
  // drafter whether to clear the textarea. The prior behaviour
  // (auto-clear) silently dropped the steering even when the
  // drafter wanted to iterate on the same instruction. The
  // dialog now offers Keep vs Clear and remembers the choice
  // per-generate (not across the session).
  const [clearSteeringOpen, setClearSteeringOpen] = useState(false);

  // BUG-039 — IDF coverage panel's "Add to section" buttons fire a
  // window event with the concept text; prefill the steering box +
  // expand it so the drafter can review/edit before clicking
  // Generate. Listener is mounted once per ActionBar instance,
  // tied to the active section so the prefill targets the section
  // the drafter is viewing.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{
        concept?: string; text?: string;
      }>)?.detail;
      // Direct-text mode (claim-anchor "Add coverage" CTA).
      // Fully replaces steering text so the multi-feature
      // directive lands cleanly without competing with whatever
      // the concept-prefix path may have appended.
      const text = (detail?.text || '').trim();
      if (text) {
        setSteeringText(text);
        setSteeringOpen(true);
        return;
      }
      // Legacy concept-prefix mode (IDF coverage dock CTA).
      const concept = (detail?.concept || '').trim();
      if (!concept) return;
      const prefix = 'Include coverage of: ';
      // Avoid stomping unrelated drafter input — append on a new
      // line if there's already content unrelated to the prefix.
      setSteeringText((cur) => {
        if (!cur) return `${prefix}${concept}`;
        if (cur.includes(prefix) && cur.includes(concept)) return cur;
        return `${cur.replace(/\n+$/, '')}\n${prefix}${concept}`;
      });
      setSteeringOpen(true);
    };
    window.addEventListener('spec.action-bar.prefill-steering', handler);
    return () => window.removeEventListener(
      'spec.action-bar.prefill-steering', handler,
    );
  }, []);

  const idx = activeSectionId
    ? sections.findIndex((s) => s.id === activeSectionId)
    : -1;
  const active = idx >= 0 ? sections[idx] : null;
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next = idx >= 0 && idx < sections.length - 1 ? sections[idx + 1] : null;

  const isGenerating = generateStatus === 'generating';
  const isMutating =
    paragraphMutationStatus === 'expanding' ||
    paragraphMutationStatus === 'tightening';
  const canMutateParagraph =
    !!active &&
    !active.is_auto_generated &&
    !active.is_frozen &&
    !!activeParagraphId &&
    !isGenerating;

  const handleExpand = useCallback(async () => {
    if (!activeParagraphId || !canMutateParagraph) return;
    const outcome = await expandParagraph({ paragraphId: activeParagraphId });
    if (outcome.kind === 'error') {
      showToast(`Expand failed: ${outcome.message}`);
    } else if (outcome.kind === 'locked') {
      showToast('Specification locked: drawings are no longer frozen.');
    }
  }, [activeParagraphId, canMutateParagraph, expandParagraph, showToast]);

  const handleTighten = useCallback(async () => {
    if (!activeParagraphId || !canMutateParagraph) return;
    const outcome = await tightenParagraph({ paragraphId: activeParagraphId });
    if (outcome.kind === 'error') {
      showToast(`Tighten failed: ${outcome.message}`);
    } else if (outcome.kind === 'locked') {
      showToast('Specification locked: drawings are no longer frozen.');
    }
  }, [activeParagraphId, canMutateParagraph, tightenParagraph, showToast]);
  // Generate is blocked when:
  //   * no active section / auto-generated / frozen — never
  //     regenerable.
  //   * already generating — protects against double-fire.
  //   * GE is in flight — single-section generates can't race
  //     the whole-doc GE worker. Without this gate the drafter
  //     can click "Generate with this steering" during GE,
  //     watch the API land a 409 section_not_empty (because
  //     GE just wrote that section), and see the
  //     OverwriteConfirmDialog ("Regenerate with steering?")
  //     pop up — which reads as "the Add-to-Section CTA
  //     triggered a regenerate prompt". Disabling Generate
  //     during GE breaks that confused chain at the source.
  const generateDisabled =
    !active ||
    active.is_auto_generated ||
    active.is_frozen ||
    isGenerating ||
    geRunning;

  // Single fire-the-generate helper. ``useSteering`` decides
  // whether the steering text actually rides along as
  // ``drafter_intent``. When false (drafter clicked "Ignore
  // steering" in the choice dialog), the textarea content stays
  // in the panel but doesn't reach the LLM. On success the
  // post-generate clear-confirm only fires when steering was
  // actually used.
  const _fireGenerate = useCallback(async (useSteering: boolean) => {
    if (!active) return;
    const skipConfirm = pendingRegenerateWithSteering;
    const intentText = useSteering ? steeringText.trim() : '';
    const outcome = await generateSection({
      sectionId: active.id,
      drafterIntent: intentText,
      overwrite: skipConfirm,
    });
    if (skipConfirm) setPendingRegenerateWithSteering(false);
    if (outcome.kind === 'ok') {
      if (useSteering && intentText) {
        // Item 5: post-generate clear-confirm. The drafter
        // explicitly applied steering — offer to clear it now
        // (vs the prior unconditional auto-clear that lost
        // iterate-on-same-instruction workflows).
        setClearSteeringOpen(true);
      } else {
        // Ignored / empty steering — collapse the panel and
        // leave the textarea alone (drafter may want to retry
        // with steering on the next click).
        setSteeringOpen(false);
      }
    } else if (outcome.kind === 'error') {
      showToast(`Generate failed: ${outcome.message}`);
    } else if (outcome.kind === 'locked') {
      showToast('Specification locked: drawings are no longer frozen.');
    }
  }, [
    active, generateSection, showToast, steeringText,
    pendingRegenerateWithSteering, setPendingRegenerateWithSteering,
  ]);

  const handleGenerate = useCallback(async () => {
    if (!active) return;
    if (active.is_auto_generated) return;
    if (active.is_frozen) {
      showToast('Unfreeze this section to regenerate.');
      return;
    }
    const hasSteeringText = steeringText.trim().length > 0;
    // Item 3 — steering disambiguation. When the drafter has
    // typed steering AND this isn't the auto-prefill banner CTA
    // (which is explicit consent), pause to confirm whether the
    // steering should ride along. Avoids the "silent use" /
    // "silent ignore" footgun the previous flow created.
    if (hasSteeringText && !pendingRegenerateWithSteering) {
      setSteeringChoiceOpen(true);
      return;
    }
    // Auto-prefill banner path OR empty steering: fire directly.
    await _fireGenerate(hasSteeringText);
  }, [
    active, showToast, steeringText, pendingRegenerateWithSteering,
    _fireGenerate,
  ]);

  // UX-F01 (Phase 27 3-F) — the button now opens the styled
  // ConfirmDialog (rendered below in the return tree) rather than
  // firing window.confirm() inline. The confirm action calls
  // ``handleResetConfirmed`` which runs the original logic.
  const handleResetClick = useCallback(() => {
    if (!active) return;
    if (active.is_auto_generated || active.is_frozen) return;
    if ((active.paragraph_count ?? 0) === 0) return;
    setConfirmResetOpen(true);
  }, [active]);

  const handleResetConfirmed = useCallback(async () => {
    if (!active) return;
    setResetting(true);
    try {
      const outcome = await resetSection(active.id);
      if (outcome.kind === 'error') {
        showToast(`Reset failed: ${outcome.message}`);
      } else {
        showToast(
          outcome.deleted > 0
            ? `Reset — ${outcome.deleted} paragraph(s) cleared.`
            : 'Section was already empty.',
        );
      }
    } finally {
      setResetting(false);
      setConfirmResetOpen(false);
    }
  }, [active, resetSection, showToast]);

  const canResetSection =
    !!active
    && !active.is_auto_generated
    && !active.is_frozen
    && (active.paragraph_count ?? 0) > 0;

  const handleConfirmOverwrite = useCallback(async () => {
    // Re-issues the original generate with overwrite=true. The
    // store re-applies the pendingOverwrite snapshot's
    // drafterIntent, so if steering was active it rides along.
    // Mirror the post-generate clear-confirm path: if steering
    // was applied, ask to clear; otherwise just collapse the
    // panel.
    const usedSteering = !!steeringText.trim();
    const outcome = await confirmGenerateOverwrite();
    if (outcome.kind === 'ok') {
      if (usedSteering) {
        setClearSteeringOpen(true);
      } else {
        setSteeringOpen(false);
      }
    } else if (outcome.kind === 'error') {
      showToast(`Generate failed: ${outcome.message}`);
    }
  }, [confirmGenerateOverwrite, showToast, steeringText]);

  const handleMergeSteering = useCallback(async () => {
    // The additive half of the overwrite dialog (2026-09-01 relook
    // P0.3). Instead of DELETE-then-INSERT, apply the drafter's
    // instruction to the paragraphs that are already there via the
    // nl-edit endpoint, which diffs per paragraph and leaves
    // untouched text alone. Until now this endpoint existed and had
    // a client function but nothing in the steering flow reached
    // it, so "Regenerate with steering" was the only wired way to
    // act on an instruction — and it always replaced.
    if (!active) return;
    const instruction = steeringText.trim();
    if (!instruction) return;
    const outcome = await applyNlEdit({
      sectionId: active.id,
      drafterRequest: instruction,
    });
    cancelGenerateOverwrite();
    if (outcome.kind === 'ok') {
      setClearSteeringOpen(true);
    } else if (outcome.kind === 'error') {
      showToast(`Merge failed: ${outcome.message}`);
    } else if (outcome.kind === 'locked') {
      showToast('Specification locked: drawings are no longer frozen.');
    }
  }, [
    active, applyNlEdit, cancelGenerateOverwrite, showToast, steeringText,
  ]);

  // Item 1 — "Regenerate →" arrow signals that steering will
  // ride along on the next click. Triggers whenever the drafter
  // has typed text in the steering box OR the banner CTA has
  // staged a payload (pendingRegenerateWithSteering). Plain
  // "Regenerate" appears only when the textarea is empty.
  const hasSteeringText = steeringText.trim().length > 0;
  const steeringPending = pendingRegenerateWithSteering || hasSteeringText;
  const generateLabel = isGenerating
    ? 'Generating…'
    : active && !active.is_auto_generated && active.paragraph_count > 0
      ? (steeringPending ? 'Regenerate →' : 'Regenerate')
      : 'Generate';

  // Phase 7 Slice 7D — auto-generated sections never expose
  // Generate / Steering / Expand / Tighten / Verify. The whole
  // mutation group is hidden so the bar shows only navigation
  // controls (Prev / Next) and the AutoGeneratedSection's own
  // "Refresh" button stays the only call-to-action.
  const isAuto = !!active && active.is_auto_generated;

  return (
    <>
      <div
        className="flex items-center gap-2 border-b border-zinc-200 bg-white px-6 py-2"
        data-testid="spec-action-bar"
      >
        {!isAuto && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={[
              primaryBtn,
              pendingRegenerateWithSteering ? 'relative' : '',
            ].join(' ').trim()}
            disabled={generateDisabled}
            onClick={handleGenerate}
            data-testid="spec-action-generate"
            data-pending-steering={pendingRegenerateWithSteering ? '1' : '0'}
            title={
              !active
                ? 'No section selected'
                : active.is_auto_generated
                  ? 'Auto-generated sections render in Phase 7'
                  : active.is_frozen
                    ? 'Frozen — unfreeze to regenerate'
                    : pendingRegenerateWithSteering
                      ? 'Regenerate with the steering loaded from the claim coverage banner'
                      : 'Generate this section'
            }
          >
            {isGenerating ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            {generateLabel}
            {pendingRegenerateWithSteering && !isGenerating && (
              <span
                className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-[#3b82f6] ring-2 ring-white"
                aria-hidden
                data-testid="spec-action-generate-pending-dot"
              />
            )}
          </button>
          <button
            type="button"
            className={btn}
            onClick={() => setSteeringOpen((v) => !v)}
            disabled={!active || active.is_auto_generated}
            data-testid="spec-action-steering-toggle"
            aria-expanded={steeringOpen}
            title={
              'Add specific instructions before regenerating. '
              + 'Steering is appended to the section prompt.'
            }
          >
            Steering {steeringOpen ? '▴' : '▾'}
          </button>
          <button
            type="button"
            className={btn}
            disabled={!canMutateParagraph || isMutating}
            onClick={handleExpand}
            data-testid="spec-action-expand"
            title={
              !active
                ? 'No section selected'
                : !activeParagraphId
                  ? 'Click a paragraph first'
                  : isMutating
                    ? 'Mutation in flight'
                    : 'Expand the active paragraph'
            }
          >
            {paragraphMutationStatus === 'expanding' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Expand
          </button>
          <button
            type="button"
            className={btn}
            disabled={!canMutateParagraph || isMutating}
            onClick={handleTighten}
            data-testid="spec-action-tighten"
            title={
              !active
                ? 'No section selected'
                : !activeParagraphId
                  ? 'Click a paragraph first'
                  : isMutating
                    ? 'Mutation in flight'
                    : 'Tighten the active paragraph'
            }
          >
            {paragraphMutationStatus === 'tightening' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Tighten
          </button>
          <button
            type="button"
            className={btn}
            disabled={
              !active
              || active.is_auto_generated
              || (active.id !== undefined
                  && sectionVerifyStatus[active.id] === 'running')
            }
            onClick={async () => {
              if (!active || active.is_auto_generated) return;
              const r = await verifySection(active.id);
              if (r.kind === 'error') {
                showToast(`Verify failed: ${r.message}`);
              } else if (r.kind === 'locked') {
                showToast('Specification locked: drawings are no longer frozen.');
              } else {
                // BUG-013 — explicit success feedback. Without
                // this, a clean verify produces zero visible UI
                // change (the amber findings banner in
                // PaperSurface only renders when issues.length >
                // 0). Drafter clicks → silence → assumes verify
                // didn't run. Read fresh issue list off the
                // store rather than threading it through the
                // verifySection return shape so we stay in sync
                // with how PaperSurface renders the banner.
                const st = useSpecStore.getState();
                const issues = st.sectionVerifyIssues[active.id] ?? [];
                const score = st.sectionVerifyScore[active.id] ?? null;
                const pctSuffix =
                  score && typeof score.completeness_pct === 'number'
                    ? ` · ${score.completeness_pct}% complete`
                    : '';
                if (issues.length === 0) {
                  showToast(`Verify complete — no issues${pctSuffix}.`);
                } else {
                  showToast(
                    `Verify complete — ${issues.length} issue${issues.length === 1 ? '' : 's'} flagged${pctSuffix}.`,
                  );
                }
              }
            }}
            data-testid="spec-action-verify"
            title="Run section verify (transient inline check)"
          >
            {active && sectionVerifyStatus[active.id] === 'running' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : null}
            Verify
          </button>
          {canResetSection && (
            <button
              type="button"
              className={
                'inline-flex h-8 items-center justify-center gap-1 '
                + 'rounded-sm border border-red-300 bg-white px-3 '
                + 'text-xs font-medium text-red-700 hover:bg-red-50 '
                + 'disabled:opacity-50 transition-colors'
              }
              disabled={resetting || isGenerating}
              onClick={handleResetClick}
              data-testid="spec-action-reset-section"
              title={
                'Permanently delete every paragraph in this section. '
                + 'A frozen section has to be unfrozen first.'
              }
            >
              {resetting
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                : <Trash2 className="h-3.5 w-3.5" />}
              {resetting ? 'Resetting…' : 'Reset section'}
            </button>
          )}
        </div>
        )}
        {/* Advisory when Generate-Everything is actively running.
            Per-section Generate is NOT gated by GE state in the
            disabled clauses (see ActionBar:185-190), so drafters
            CAN still drive single-section generates while the
            corner card shows GE progress. The previous UX gave
            no signal of that — a stuck GE row made the workbench
            feel frozen. This line surfaces the parallel-track
            option inline. Auto-hidden whenever GE isn't active. */}
        {!isAuto && geRunning && (
          <p
            className="text-[11px] text-amber-700 mt-1"
            data-testid="spec-action-ge-advisory"
          >
            Generate-everything is in progress — individual section
            Generate is still available.
          </p>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            className={btn}
            disabled={examinerStatus === 'running'}
            onClick={async () => {
              const r = await runExaminerReview();
              if (r.kind === 'error') {
                showToast(`Examiner review failed: ${r.message ?? ''}`);
              } else if (r.kind === 'locked') {
                showToast('Specification locked: drawings are no longer frozen.');
              } else {
                const n = r.openCount ?? 0;
                const found = n === 0
                  ? 'no open issues'
                  : `${n} open issue${n === 1 ? '' : 's'} — listed in Examiner view on the right`;
                showToast(r.kind === 'cached'
                  ? `Examiner review (from the run a moment ago): ${found}.`
                  : `Examiner review complete: ${found}.`);
              }
            }}
            data-testid="spec-action-examiner-review"
            // BUG-014 — surface the disabled-reason so the user
            // can tell when the button is gated by an in-flight
            // run vs simply doing nothing.
            title={
              examinerStatus === 'running'
                ? 'Examiner review already running — please wait.'
                : 'Run matter-wide examiner-mode review (whole spec)'
            }
          >
            {examinerStatus === 'running' ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldCheck className="h-3.5 w-3.5" />
            )}
            Examiner
          </button>
        </div>
        <div className="ml-2 flex items-center gap-2">
          <button
            type="button"
            className={btn}
            disabled={!prev}
            onClick={() => {
              if (!prev) return;
              setActiveSection(prev.id);
              // BUG-045 — scroll the centre pane to the prev/next
              // section block; PaperEditor listens for the
              // window event so the smooth scroll lands.
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(
                  'spec.scroll-to-section', { detail: { id: prev.id } },
                ));
              }
            }}
            data-testid="spec-action-prev"
            aria-label="Previous section"
            title={prev ? `Previous: ${prev.title}` : 'No previous section'}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Prev
          </button>
          <button
            type="button"
            className={btn}
            disabled={!next}
            onClick={() => {
              if (!next) return;
              setActiveSection(next.id);
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent(
                  'spec.scroll-to-section', { detail: { id: next.id } },
                ));
              }
            }}
            data-testid="spec-action-next"
            aria-label="Next section"
            title={next ? `Next: ${next.title}` : 'No next section'}
          >
            Next
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {steeringOpen && !isAuto && (
        <div
          className="border-b border-zinc-200 bg-zinc-50 px-6 py-2"
          data-testid="spec-action-steering"
        >
          <label className="block text-[11px] font-medium text-zinc-600 mb-1">
            Steering — optional guidance for this generation
          </label>
          <textarea
            className="w-full rounded-sm border border-zinc-300 bg-white px-2 py-1 text-xs text-zinc-700 placeholder:text-zinc-400 outline-none focus:border-zinc-500"
            rows={2}
            value={steeringText}
            onChange={(e) => setSteeringText(e.target.value)}
            placeholder="e.g. emphasise calibration step; cite FIG. 4 explicitly"
            data-testid="spec-action-steering-textarea"
            disabled={isGenerating}
          />
          {/* BUG-012 — inline Generate-with-steering action so the
              drafter doesn't have to hunt for the top-bar primary
              button. Same handler + same disabled rules as the
              top-bar Generate; no separate code path. The "Clear"
              button lets the drafter dismiss an auto-prefilled
              directive (from the claim coverage banner or IDF
              dock) without firing generation. */}
          <div className="mt-2 flex items-center justify-between gap-2">
            <p
              className="text-[10px] text-zinc-500"
              data-testid="spec-action-steering-hint"
            >
              {isGenerating
                ? 'Generation in flight — wait for it to finish, then your steering will fire on the next click.'
                : geRunning
                  ? 'Generate-Everything is running. Steering will stay loaded; click Generate after it completes.'
                  : pendingRegenerateWithSteering
                    ? 'Auto-loaded from the claim coverage banner. Edit if needed, then click Generate with this steering.'
                    : 'Steering is appended to the section prompt as drafter_intent.'}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className={btn}
                onClick={() => {
                  setSteeringText('');
                  if (pendingRegenerateWithSteering) {
                    setPendingRegenerateWithSteering(false);
                  }
                }}
                disabled={isGenerating || !steeringText}
                data-testid="spec-action-steering-clear"
                title="Clear the steering textarea without firing generation."
              >
                Clear
              </button>
              <button
                type="button"
                className={primaryBtn}
                onClick={async () => {
                  if (!active) return;
                  if (active.is_auto_generated || active.is_frozen) return;
                  // Inline panel button is itself the "use
                  // steering" affordance — bypass the
                  // steering-choice dialog and fire directly so
                  // the drafter doesn't get a redundant prompt.
                  await _fireGenerate(true);
                }}
                disabled={generateDisabled || !steeringText.trim()}
                data-testid="spec-action-steering-generate"
                title={
                  !steeringText.trim()
                    ? 'Type or paste steering text first.'
                    : isGenerating
                      ? 'Generation in flight.'
                      : 'Run section generate with this steering as drafter_intent.'
                }
              >
                {isGenerating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                {isGenerating ? 'Generating…' : 'Generate with this steering'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingOverwrite && active && (
        <OverwriteConfirmDialog
          sectionTitle={active.title}
          paragraphCount={pendingOverwrite.paragraphCount}
          onConfirm={handleConfirmOverwrite}
          onMerge={handleMergeSteering}
          onCancel={cancelGenerateOverwrite}
          isGenerating={isGenerating}
          hasSteering={!!steeringText.trim()}
        />
      )}

      {/* UX-F01 (Phase 27 3-F) — styled Reset-section confirm.
          Replaces the prior window.confirm() at this call site;
          the dialog is rendered as a sibling of the action-bar
          tree so the modal's z-index sits above the canvas + the
          right-rail context panels. */}
      {confirmResetOpen && active && (
        <ConfirmDialog
          title={`Reset "${active.title}"?`}
          message={
            `All ${active.paragraph_count ?? 0} generated paragraph(s) `
            + 'will be permanently deleted. This cannot be undone.'
          }
          confirmLabel="Reset section"
          isPending={resetting}
          onConfirm={() => { void handleResetConfirmed(); }}
          onCancel={() => setConfirmResetOpen(false)}
          testIdSuffix="spec-action-reset-section-confirm"
        />
      )}

      {/* Item 3 — Steering disambiguation. Three options: use
          the loaded steering, ignore it (regenerate without
          drafter_intent), or cancel. ConfirmDialog only supports
          2 buttons, so this dialog is rendered inline with the
          same backdrop/panel pattern. */}
      {steeringChoiceOpen && active && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
          role="dialog"
          aria-modal="true"
          aria-label="Use steering or ignore it?"
          data-testid="spec-action-steering-choice"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isGenerating) {
              setSteeringChoiceOpen(false);
            }
          }}
        >
          <div
            className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="mb-2 text-base font-semibold text-zinc-900">
              You have active steering
            </h2>
            <p className="mb-3 text-sm leading-relaxed text-zinc-600">
              Steering text is loaded in the panel. Regenerate
              with it, or run a plain regenerate that ignores the
              steering for this turn?
            </p>
            <blockquote className="mb-5 rounded-sm border-l-2 border-zinc-300 bg-zinc-50 px-3 py-2 text-xs italic text-zinc-700">
              {steeringText.trim().length > 200
                ? `${steeringText.trim().slice(0, 200)}…`
                : steeringText.trim()}
            </blockquote>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                className={btn}
                disabled={isGenerating}
                onClick={() => setSteeringChoiceOpen(false)}
                data-testid="spec-action-steering-choice-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                className={btn}
                disabled={isGenerating}
                onClick={async () => {
                  setSteeringChoiceOpen(false);
                  await _fireGenerate(false);
                }}
                data-testid="spec-action-steering-choice-ignore"
                title="Regenerate without applying the steering text"
              >
                Ignore steering
              </button>
              <button
                type="button"
                className={primaryBtn}
                disabled={isGenerating}
                onClick={async () => {
                  setSteeringChoiceOpen(false);
                  await _fireGenerate(true);
                }}
                data-testid="spec-action-steering-choice-use"
                title="Regenerate with the steering text applied as drafter_intent"
              >
                {isGenerating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" />
                )}
                Use steering
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item 5 — post-generate clear-steering confirm. Fires
          only when the just-completed regenerate actually used
          steering. "Keep" leaves the textarea intact so the
          drafter can iterate on the same instruction; "Clear"
          empties it. */}
      {clearSteeringOpen && (
        <ConfirmDialog
          title="Steering applied"
          message={
            'The section was regenerated with your steering text. '
            + 'Clear the steering panel now, or keep it for another '
            + 'pass?'
          }
          confirmLabel="Clear steering"
          cancelLabel="Keep"
          onConfirm={() => {
            setSteeringText('');
            setSteeringOpen(false);
            setClearSteeringOpen(false);
          }}
          onCancel={() => setClearSteeringOpen(false)}
          testIdSuffix="spec-action-clear-steering-confirm"
        />
      )}
    </>
  );
}
